import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLevelInfo, getNextLevelInfo } from '@/mocks/mileageData';
import { clearStoredLookupToken, DEFAULT_SITE_CODE, fetchDailyAccrual, fetchMyBalance, fetchRanking, getStoredLookupToken, LookupTokenInvalidError, type DailyAccrualResponse, type MileageBalance, type RankingResponse } from '@/lib/api';
import mileageGuide from '@/assets/mileage-guide.jpg';

// 명예의 전당(TOP 20 랭킹표) 노출 여부. 운영 요청으로 임시 숨김 — 다시 보이게
// 하려면 true로만 바꾸면 된다. (본인 "현재 순위" 표시는 그대로 유지된다.)
const SHOW_HALL_OF_FAME = false;

// 화면 표시용 소수 1자리 반올림. 4.499999… → "4.5", 4 → "4"(불필요한 .0 제거).
// 서버 합산 과정에서 이진 부동소수점 노이즈가 낄 수 있어 표시 경계에서 정리한다.
const fmt1 = (v: number) => String(Math.round(Number(v) * 10) / 10);

export default function MileagePage() {
  const navigate = useNavigate();
  const [balance, setBalance] = useState<MileageBalance | null>(null);
  const [ranking, setRanking] = useState<RankingResponse | null>(null);
  const [daily, setDaily] = useState<DailyAccrualResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const lookupToken = getStoredLookupToken();
    if (!lookupToken) {
      navigate('/');
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const [balanceData, rankingData, dailyData] = await Promise.all([
          fetchMyBalance(lookupToken),
          fetchRanking(lookupToken, DEFAULT_SITE_CODE, 20),
          fetchDailyAccrual(lookupToken),
        ]);

        if (cancelled) return;
        setBalance(balanceData);
        setRanking(rankingData);
        setDaily(dailyData);
      } catch (error) {
        if (cancelled) return;
        if (error instanceof LookupTokenInvalidError) {
          // Token expired (~30min) or is otherwise invalid — there is no
          // silent refresh in a no-auth model, so send them back to type
          // their ID again rather than show a dead balance screen.
          clearStoredLookupToken();
          navigate('/', { replace: true });
          return;
        }
        setErrorMsg('마일리지 정보를 불러오지 못했습니다. 잠시 후 다시 시도해주세요.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-primary-50/40 flex items-center justify-center">
        <i className="ri-loader-4-line text-3xl text-primary-500 animate-spin"></i>
      </div>
    );
  }

  if (errorMsg || !balance || !ranking || !daily) {
    return (
      <div className="min-h-screen bg-primary-50/40 flex items-center justify-center px-5">
        <div className="flex items-center gap-3 px-4 py-3.5 rounded-xl bg-red-50 border border-red-100 max-w-sm">
          <div className="w-7 h-7 shrink-0 rounded-full bg-red-100 flex items-center justify-center">
            <i className="ri-error-warning-fill text-sm text-red-500"></i>
          </div>
          <p className="text-sm text-red-600 font-medium leading-relaxed">
            {errorMsg || '마일리지 정보를 불러오지 못했습니다.'}
          </p>
        </div>
      </div>
    );
  }

  const levelInfo = getLevelInfo(balance.accumulated_mileage);
  const nextInfo = getNextLevelInfo(balance.accumulated_mileage);

  const formatDailyDate = (isoDate: string): string => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
    if (!match) return isoDate;
    const month = Number(match[2]);
    const day = Number(match[3]);
    return `${month}월 ${day}일`;
  };

  return (
    <div className="min-h-screen bg-primary-50/40">
      <div className="max-w-6xl mx-auto px-5 py-8 md:py-12">
        {/* Page Title */}
        <div className="mb-8 md:mb-10">
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
              마일리지
            </h1>
            <p className="text-sm text-foreground-500 mt-1">
              학습 활동으로 쌓은 마일리지를 확인하고 랭킹을 비교해보세요
            </p>
            {/* 주의사항 — 사용자 인지 위해 깜빡임 처리 */}
            <div className="mt-3 rounded-lg bg-amber-50 border border-amber-300 px-3.5 py-2.5 animate-pulse">
              <p className="text-xs md:text-sm text-amber-800 font-medium leading-relaxed">
                ※ 해당 마일리지(순위)는 복꿈페 참여 점수에 한정되며, 복권기금 꿈사다리 장학사업 전체 마일리지 점수와는 별도로 산정되는 점 참고 부탁드리겠습니다.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left: 내 마일리지 — 명예의 전당을 숨긴 경우 가운데로 넓게 배치 */}
          <div className={SHOW_HALL_OF_FAME ? 'w-full lg:w-[380px] shrink-0' : 'w-full lg:max-w-[480px] lg:mx-auto'}>
            <div className="bg-background-50 rounded-2xl border-2 border-primary-100 overflow-hidden">
              {/* Character Area */}
              <div className="relative bg-gradient-to-b from-primary-100/60 to-primary-50/30 px-6 pt-8 pb-6 flex flex-col items-center">
                <div className={`px-4 py-1.5 rounded-full text-sm font-semibold ${levelInfo.bgColor} ${levelInfo.textColor} border ${levelInfo.borderColor} mb-4`}>
                  {levelInfo.label}
                </div>

                {/* Character Image */}
                <div className="w-40 h-40 md:w-48 md:h-48 rounded-full overflow-hidden bg-background-50 border-4 border-background-50">
                  <img
                    src={levelInfo.image}
                    alt={levelInfo.alt}
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                {/* Mileage Score */}
                <div className="mt-5 text-center">
                  <p className="text-sm text-foreground-500 mb-1">보유 마일리지</p>
                  <p className="text-4xl md:text-5xl font-bold text-foreground-950 font-heading">
                    {fmt1(balance.accumulated_mileage)}
                    <span className="text-xl text-foreground-400 font-body ml-1">점</span>
                  </p>
                </div>

                {/* Progress Bar */}
                {nextInfo.progress < 100 && (
                  <div className="w-full mt-5">
                    <div className="flex items-center justify-between text-xs text-foreground-500 mb-1.5">
                      <span>다음 레벨: {nextInfo.nextLabel}</span>
                      <span>앞으로 {fmt1(nextInfo.remaining)}점</span>
                    </div>
                    <div className="w-full h-2.5 rounded-full bg-secondary-200 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-primary-500 transition-all duration-700"
                        style={{ width: `${nextInfo.progress}%` }}
                      />
                    </div>
                  </div>
                )}

                {nextInfo.progress >= 100 && (
                  <div className="mt-5 w-full text-center">
                    <div className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-amber-50 border border-amber-200">
                      <i className="ri-vip-crown-fill text-amber-500 text-base"></i>
                      <span className="text-sm font-semibold text-amber-700">{nextInfo.nextLabel}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Current Rank */}
              <div className="px-6 py-4 border-t border-primary-100/60">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-foreground-500">현재 순위</span>
                  <span className="text-lg font-bold text-foreground-950 font-heading">
                    {ranking.my_rank !== null ? (
                      <>
                        {ranking.my_rank}위
                        <span className="text-sm text-foreground-400 font-body ml-1">
                          / 전체 {ranking.total_users}명 중
                        </span>
                      </>
                    ) : (
                      <span className="text-sm text-foreground-400 font-body">순위 집계 전</span>
                    )}
                  </span>
                </div>
                {ranking.snapshot_date && (
                  <p className="text-xs text-foreground-400 font-body mt-1 text-right">
                    기준: {ranking.snapshot_date} 00시 집계
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Right: Ranking Table — SHOW_HALL_OF_FAME 로 노출 제어 */}
          {SHOW_HALL_OF_FAME && (
          <div className="flex-1 min-w-0">
            <div className="bg-background-50 rounded-2xl border-2 border-primary-100 overflow-hidden">
              <div className="px-4 md:px-6 py-4 border-b border-primary-100/60 flex items-center gap-2">
                <i className="ri-trophy-line text-lg text-primary-500"></i>
                <h2 className="text-base font-bold text-foreground-950 font-heading">명예의 전당 TOP 20</h2>
              </div>

              {/* Table Header */}
              <div className="hidden md:flex items-center px-6 py-3 bg-background-100/60 border-b border-background-200/70 text-xs text-foreground-400 font-medium">
                <span className="w-12 text-center">순위</span>
                <span className="flex-1">이름</span>
                <span className="w-28 text-center">점수</span>
                <span className="w-24 text-center">레벨</span>
              </div>

              {/* Ranking Rows */}
              <div className="divide-y divide-background-100">
                {ranking.top.map((entry) => {
                  const info = getLevelInfo(entry.mileage);
                  const isMe = entry.is_me;
                  const isTop3 = entry.rank <= 3;

                  return (
                    <div
                      key={entry.rank}
                      className={`flex items-center px-4 md:px-6 py-3 transition-colors ${
                        isMe ? 'bg-primary-50/60 border-l-4 border-l-primary-500' : 'hover:bg-background-50'
                      } ${isTop3 && !isMe ? 'bg-amber-50/20' : ''}`}
                    >
                      {/* Rank */}
                      <div className="w-8 md:w-12 shrink-0 flex items-center justify-center">
                        {isTop3 ? (
                          <span className={`text-lg ${entry.rank === 1 ? 'text-amber-500' : entry.rank === 2 ? 'text-foreground-400' : 'text-amber-700'}`}>
                            {entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : '🥉'}
                          </span>
                        ) : (
                          <span className="text-sm font-semibold text-foreground-500">{entry.rank}</span>
                        )}
                      </div>

                      {/* Name */}
                      <div className="flex-1 min-w-0 flex items-center gap-1.5 md:gap-2.5">
                        <span className={`text-sm font-medium truncate whitespace-nowrap ${isMe ? 'text-primary-700 font-bold' : 'text-foreground-800'}`}>
                          {entry.user_nm}
                        </span>
                        {isMe && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary-500 text-background-50 font-semibold whitespace-nowrap">
                            나
                          </span>
                        )}
                      </div>

                      {/* Score */}
                      <div className="w-14 md:w-28 shrink-0 text-center">
                        <span className={`text-sm font-bold font-heading ${isMe ? 'text-primary-600' : 'text-foreground-700'}`}>
                          {fmt1(entry.mileage)}점
                        </span>
                      </div>

                      {/* Level Badge */}
                      <div className="w-14 md:w-24 shrink-0 flex justify-center">
                        <span className={`inline-block text-xs px-2 py-1 rounded-full font-semibold ${info.bgColor} ${info.textColor} border ${info.borderColor} whitespace-nowrap`}>
                          {info.label}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          )}
        </div>

        {/* 일별 적립 내역 */}
        <div className="mt-8">
          <div className="bg-background-50 rounded-2xl border-2 border-primary-100 overflow-hidden">
            <div className="px-4 md:px-6 py-4 border-b border-primary-100/60 flex items-center gap-2">
              <i className="ri-calendar-check-line text-lg text-primary-500"></i>
              <h2 className="text-base font-bold text-foreground-950 font-heading">일별 적립 내역</h2>
            </div>

            {daily.daily.length === 0 ? (
              <div className="px-6 py-12 flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mb-3">
                  <i className="ri-inbox-line text-xl text-foreground-400"></i>
                </div>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  아직 적립 내역이 없습니다.<br />
                  매일 00시에 그날 적립이 확정됩니다.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-background-100">
                {daily.daily.map((entry) => (
                  <div
                    key={entry.date}
                    className="flex items-center px-4 md:px-6 py-3.5 hover:bg-background-50 transition-colors"
                  >
                    {/* Date */}
                    <div className="w-24 md:w-32 shrink-0">
                      <span className="text-sm font-semibold text-foreground-800 font-heading">
                        {formatDailyDate(entry.date)}
                      </span>
                    </div>

                    {/* Note */}
                    <div className="flex-1 min-w-0">
                      {entry.note && (
                        <span className="text-sm text-foreground-500 truncate">{entry.note}</span>
                      )}
                    </div>

                    {/* Amount */}
                    <div className="shrink-0 text-right">
                      <span className="text-sm font-bold font-heading text-primary-600">
                        +{fmt1(entry.amount)}
                        <span className="text-xs text-foreground-400 font-body ml-0.5">점</span>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 마일리지 적립 안내 이미지 — 일별 적립 내역 아래에 배치 */}
        <div className="mt-8">
          <img
            src={mileageGuide}
            alt="복꿈 마일리지 적립 안내"
            className="w-full rounded-2xl border-2 border-primary-100"
          />
        </div>

        {/* Level Guide — 맨 아래 배치 */}
        <div className="mt-8">
          <div className="bg-background-50 rounded-2xl border-2 border-primary-100 p-6">
            <h3 className="text-base font-bold text-foreground-950 font-heading mb-4">레벨 안내</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: '레벨 1', range: '1~5점', color: 'bg-sky-500', bgColor: 'bg-sky-50', textColor: 'text-sky-700', borderColor: 'border-sky-200', icon: 'ri-seedling-line' },
                { label: '레벨 2', range: '6~10점', color: 'bg-teal-500', bgColor: 'bg-teal-50', textColor: 'text-teal-700', borderColor: 'border-teal-200', icon: 'ri-plant-line' },
                { label: '레벨 3', range: '11~15점', color: 'bg-emerald-500', bgColor: 'bg-emerald-50', textColor: 'text-emerald-700', borderColor: 'border-emerald-200', icon: 'ri-flower-line' },
                { label: '마스터', range: '16점 이상', color: 'bg-amber-500', bgColor: 'bg-amber-50', textColor: 'text-amber-700', borderColor: 'border-amber-200', icon: 'ri-vip-crown-fill' },
              ].map((tier) => (
                <div key={tier.label} className={`flex flex-col items-center gap-3 p-4 rounded-xl ${tier.bgColor} border ${tier.borderColor}`}>
                  <div className={`w-10 h-10 rounded-full ${tier.color} flex items-center justify-center`}>
                    <i className={`${tier.icon} text-lg text-background-50`}></i>
                  </div>
                  <div className="text-center">
                    <p className={`text-sm font-bold ${tier.textColor}`}>{tier.label}</p>
                    <p className="text-xs text-foreground-500 mt-0.5">{tier.range}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
